import React, { forwardRef } from 'react'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import { InputAdornment } from '@mui/material'

interface NumberMaterialInputProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value: number | string
  onChange: (value: number | undefined) => void
  adornment?: string
  min?: number
  maxLength?: number
  max?: number
  type?: 'currency' | 'percent' | 'integer'
  disableConvertMaxOnBlur?: boolean // Nova prop opcional
}

export const NumberMaterialInput = forwardRef<HTMLInputElement, NumberMaterialInputProps>(
  (
    {
      value,
      onChange,
      adornment,
      min,
      max,
      maxLength,
      type = 'currency',
      onBlur,
      disableConvertMaxOnBlur,
      ...props
    },
    ref
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value.replace(/[^0-9]/g, '')
      if (!rawValue && rawValue !== '0') {
        onChange(0)
        return
      }
      let numericValue: number
      if (type === 'integer') {
        numericValue = parseInt(rawValue, 10)
      } else {
        numericValue = parseFloat(rawValue) / 100
      }

      // Limitar o valor máximo no handleChange
      if (max !== undefined && numericValue > max) {
        return
      }

      onChange(numericValue)
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      let numericValue = (value as number) || undefined
      if (!numericValue) {
        numericValue = undefined
      }
      // Verificação de valor mínimo sempre aplicada
      if (!isNaN(min as number) && Number(numericValue) < min!) {
        numericValue = min! // Ajusta para o mínimo se for menor
      }

      // Condicional para converter para o valor máximo, apenas se disableConvertMaxOnBlur não estiver ativado
      if (!disableConvertMaxOnBlur) {
        if (!isNaN(max as number) && Number(numericValue) > max!) {
          numericValue = max! // Ajuste para o valor máximo se exceder e a prop não for passada
        }
      }

      onChange(numericValue)

      if (onBlur) {
        onBlur(event)
      }
    }

    function formatFloatNumber(value: number | string) {
      if (typeof value === 'string') {
        value = parseFloat(value.replace(/[^0-9]/g, '')) / 100
      }
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2
      }).format(value || 0)
    }

    return (
      <TextField
        {...props}
        ref={ref}
        value={
          type === 'currency' || type === 'percent'
            ? formatFloatNumber(value)
            : value === 0 || value
            ? value
            : ''
        }
        onChange={handleChange}
        onBlur={handleBlur}
        InputProps={{
          startAdornment: <InputAdornment position="start">{adornment ?? ''}</InputAdornment>
        }}
        inputProps={{
          maxLength: maxLength
        }}
      />
    )
  }
)

NumberMaterialInput.displayName = 'NumberMaterialInput'
